import { createTheme } from "@mui/material/styles";
import { ThemeProvider } from "@mui/material";
import React, {
  useState,
  createContext,
  useMemo,
  useContext,
  useEffect,
} from "react";
import { MainContext } from "../context/main";
import { parseCookies, setCookie } from "nookies";
import { defaultTheme, ThemeConfig } from "./palette";
import moment from "moment";
import ComponentsOverrides from "./overrides";
import {
  font200,
  font300,
  font400,
  font500,
  font700,
  font900,
} from "../styles/fontFamily";

export const themeStorage = "theme";

declare module "@mui/material/styles" {
  interface Palette {
    gradient?: any;
    secondary1?: any;
    secondary2?: any;
    glassmorphism?: any;
    gray: any;
    other: any;
  }

  interface PaletteOptions {
    gradient?: any;
    secondary1?: any;
    secondary2?: any;
    glassmorphism?: any;
    gray: any;
    other: any;
  }
  interface Theme {
    config?: any;
  }
  interface ThemeOptions {
    config?: any;
  }
  interface TypographyVariants {
    H1_ExtraLight_40px: React.CSSProperties;
    H1_ExtraLight_36px: React.CSSProperties;
    H1_ExtraLight_32px: React.CSSProperties;
    H2_ExtraLight_28px: React.CSSProperties;
    H3_ExtraLight_25px: React.CSSProperties;
    H4_ExtraLight_22px: React.CSSProperties;
    H5_ExtraLight_20px: React.CSSProperties;
    H6_ExtraLight_18px: React.CSSProperties;
    Paragraph_ExtraLight_16px: React.CSSProperties;
    Paragraph_ExtraLight_14px: React.CSSProperties;
    Hint_ExtraLight_12px: React.CSSProperties;
    Hint_ExtraLight_10px: React.CSSProperties;

    H1_Light_40px: React.CSSProperties;
    H1_Light_36px: React.CSSProperties;
    H1_Light_32px: React.CSSProperties;
    H2_Light_28px: React.CSSProperties;
    H3_Light_25px: React.CSSProperties;
    H4_Light_22px: React.CSSProperties;
    H5_Light_20px: React.CSSProperties;
    H6_Light_18px: React.CSSProperties;
    Paragraph_Light_16px: React.CSSProperties;
    Paragraph_Light_14px: React.CSSProperties;
    Paragraph_Light_12x: React.CSSProperties;
    Hint_Light_12px: React.CSSProperties;
    Hint_Light_10px: React.CSSProperties;

    H1_Regular_40px: React.CSSProperties;
    H1_Regular_36px: React.CSSProperties;
    H1_Regular_32px: React.CSSProperties;
    H2_Regular_28px: React.CSSProperties;
    H3_Regular_25px: React.CSSProperties;
    H4_Regular_22px: React.CSSProperties;
    H5_Regular_20px: React.CSSProperties;
    H6_Regular_18px: React.CSSProperties;
    Paragraph_Regular_16px: React.CSSProperties;
    Paragraph_Regular_14px: React.CSSProperties;
    Hint_Regular_12px: React.CSSProperties;
    Hint_Regular_10px: React.CSSProperties;

    H1_Medium_40px: React.CSSProperties;
    H1_Medium_36px: React.CSSProperties;
    H1_Medium_32px: React.CSSProperties;
    H2_Medium_28px: React.CSSProperties;
    H3_Medium_25px: React.CSSProperties;
    H4_Medium_22px: React.CSSProperties;
    H5_Medium_20px: React.CSSProperties;
    H6_Medium_18px: React.CSSProperties;
    Paragraph_Medium_16px: React.CSSProperties;
    Paragraph_Medium_14px: React.CSSProperties;
    Hint_Medium_12px: React.CSSProperties;
    Hint_Medium_10px: React.CSSProperties;
    H1_Bold_40px: React.CSSProperties;
    H1_Bold_36px: React.CSSProperties;
    H1_Bold_32px: React.CSSProperties;
    H2_Bold_28px: React.CSSProperties;
    H3_Bold_25px: React.CSSProperties;
    H4_Bold_22px: React.CSSProperties;
    H5_Bold_20px: React.CSSProperties;
    H6_Bold_18px: React.CSSProperties;
    Paragraph_Bold_16px: React.CSSProperties;
    Paragraph_Bold_14px: React.CSSProperties;
    Hint_Bold_12px: React.CSSProperties;

    H1_Black_40px: React.CSSProperties;
    H1_Black_36px: React.CSSProperties;
    H1_Black_32px: React.CSSProperties;
    H2_Black_28px: React.CSSProperties;
    H3_Black_25px: React.CSSProperties;
    H4_Black_22px: React.CSSProperties;
    H5_Black_20px: React.CSSProperties;
    H6_Black_18px: React.CSSProperties;
    Paragraph_Black_16px: React.CSSProperties;
    Paragraph_Black_14px: React.CSSProperties;
    Hint_Black_12px: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    H1_ExtraLight_40px: React.CSSProperties;
    H1_ExtraLight_36px: React.CSSProperties;
    H1_ExtraLight_32px: React.CSSProperties;
    H2_ExtraLight_28px: React.CSSProperties;
    H3_ExtraLight_25px: React.CSSProperties;
    H4_ExtraLight_22px: React.CSSProperties;
    H5_ExtraLight_20px: React.CSSProperties;
    H6_ExtraLight_18px: React.CSSProperties;
    Paragraph_ExtraLight_16px: React.CSSProperties;
    Paragraph_ExtraLight_14px: React.CSSProperties;
    Hint_ExtraLight_12px: React.CSSProperties;
    Hint_ExtraLight_10px: React.CSSProperties;

    H1_Light_40px: React.CSSProperties;
    H1_Light_36px: React.CSSProperties;
    H1_Light_32px: React.CSSProperties;
    H2_Light_28px: React.CSSProperties;
    H3_Light_25px: React.CSSProperties;
    H4_Light_22px: React.CSSProperties;
    H5_Light_20px: React.CSSProperties;
    H6_Light_18px: React.CSSProperties;
    Paragraph_Light_16px: React.CSSProperties;
    Paragraph_Light_14px: React.CSSProperties;
    Paragraph_Light_12px: React.CSSProperties;
    Hint_Light_12px: React.CSSProperties;
    Hint_Light_10px: React.CSSProperties;

    H1_Regular_40px: React.CSSProperties;
    H1_Regular_36px: React.CSSProperties;
    H1_Regular_32px: React.CSSProperties;
    H2_Regular_28px: React.CSSProperties;
    H3_Regular_25px: React.CSSProperties;
    H4_Regular_22px: React.CSSProperties;
    H5_Regular_20px: React.CSSProperties;
    H6_Regular_18px: React.CSSProperties;
    Paragraph_Regular_16px: React.CSSProperties;
    Paragraph_Regular_14px: React.CSSProperties;
    Hint_Regular_12px: React.CSSProperties;
    Hint_Regular_10px: React.CSSProperties;

    H1_Medium_40px: React.CSSProperties;
    H1_Medium_36px: React.CSSProperties;
    H1_Medium_32px: React.CSSProperties;
    H2_Medium_28px: React.CSSProperties;
    H3_Medium_25px: React.CSSProperties;
    H4_Medium_22px: React.CSSProperties;
    H5_Medium_20px: React.CSSProperties;
    H6_Medium_18px: React.CSSProperties;
    Paragraph_Medium_16px: React.CSSProperties;
    Paragraph_Medium_14px: React.CSSProperties;
    Hint_Medium_12px: React.CSSProperties;
    Hint_Medium_10px: React.CSSProperties;
    H1_Bold_40px: React.CSSProperties;
    H1_Bold_36px: React.CSSProperties;
    H1_Bold_32px: React.CSSProperties;
    H2_Bold_28px: React.CSSProperties;
    H3_Bold_25px: React.CSSProperties;
    H4_Bold_22px: React.CSSProperties;
    H5_Bold_20px: React.CSSProperties;
    H6_Bold_18px: React.CSSProperties;
    Paragraph_Bold_16px: React.CSSProperties;
    Paragraph_Bold_14px: React.CSSProperties;
    Hint_Bold_12px: React.CSSProperties;

    H1_Black_40px: React.CSSProperties;
    H1_Black_36px: React.CSSProperties;
    H1_Black_32px: React.CSSProperties;
    H2_Black_28px: React.CSSProperties;
    H3_Black_25px: React.CSSProperties;
    H4_Black_22px: React.CSSProperties;
    H5_Black_20px: React.CSSProperties;
    H6_Black_18px: React.CSSProperties;
    Paragraph_Black_16px: React.CSSProperties;
    Paragraph_Black_14px: React.CSSProperties;
    Hint_Black_12px: React.CSSProperties;
  }
}
declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    containedPrimary?: true;
    containedSecondary1?: true;
  }
  interface ButtonPropsColorOverrides {
    color?: true;
    shadow?: true;
    neon?: true;
    borderde?: true;
    gradient3?: true;
    gradient7?: true;
    error?: any;
  }
}
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    H1_ExtraLight_40px: true;
    H1_ExtraLight_36px: true;
    H1_ExtraLight_32px: true;
    H2_ExtraLight_28px: true;
    H3_ExtraLight_25px: true;
    H4_ExtraLight_22px: true;
    H5_ExtraLight_20px: true;
    H6_ExtraLight_18px: true;
    Paragraph_ExtraLight_16px: true;
    Paragraph_ExtraLight_14px: true;
    Hint_ExtraLight_12px: true;
    Hint_ExtraLight_10px: true;

    H1_Light_40px: true;
    H1_Light_36px: true;
    H1_Light_32px: true;
    H2_Light_28px: true;
    H3_Light_25px: true;
    H4_Light_22px: true;
    H5_Light_20px: true;
    H6_Light_18px: true;
    Paragraph_Light_16px: true;
    Paragraph_Light_14px: true;
    Paragraph_Light_12px: true;
    Hint_Light_12px: true;
    Hint_Light_10px: true;

    H1_Regular_40px: true;
    H1_Regular_36px: true;
    H1_Regular_32px: true;
    H2_Regular_28px: true;
    H3_Regular_25px: true;
    H4_Regular_22px: true;
    H5_Regular_20px: true;
    H6_Regular_18px: true;
    Paragraph_Regular_16px: true;
    Paragraph_Regular_14px: true;
    Hint_Regular_12px: true;
    Hint_Regular_10px: true;

    H1_Medium_40px: true;
    H1_Medium_36px: true;
    H1_Medium_32px: true;
    H2_Medium_28px: true;
    H3_Medium_25px: true;
    H4_Medium_22px: true;
    H5_Medium_20px: true;
    H6_Medium_18px: true;
    Paragraph_Medium_16px: true;
    Paragraph_Medium_14px: true;
    Hint_Medium_12px: true;
    Hint_Medium_10px: true;
    H1_Bold_40px: true;
    H1_Bold_36px: true;
    H1_Bold_32px: true;
    H2_Bold_28px: true;
    H3_Bold_25px: true;
    H4_Bold_22px: true;
    H5_Bold_20px: true;
    H6_Bold_18px: true;
    Paragraph_Bold_16px: true;
    Paragraph_Bold_14px: true;
    Hint_Bold_12px: true;

    H1_Black_40px: true;
    H1_Black_36px: true;
    H1_Black_32px: true;
    H2_Black_28px: true;
    H3_Black_25px: true;
    H4_Black_22px: true;
    H5_Black_20px: true;
    H6_Black_18px: true;
    Paragraph_Black_16px: true;
    Paragraph_Black_14px: true;
    Hint_Black_12px: true;
  }
}

declare module "@mui/material/TextField" {
  interface TextFieldPropsColorOverrides {
    glassmorphism: true;
    gray: true;
  }

  interface TextFieldPropsVariantOverrides {
    glassmorphism: true;
    gray: true;
  }
}

interface IThemeContext {
  settheme: Function;
  loading: boolean;
}
const initalState: IThemeContext = {
  settheme: () => undefined,
  loading: false,
};

// allCookie[process.env.NEXT_PUBLIC_THEME as any] === "dark"
// ? darkTheme
// : lightTheme,

export const ThemeContext = createContext<IThemeContext>(initalState);
export const ThemeContextProvider = (props: any) => {
  const cookies = parseCookies();
  const [loading, setloading] = useState<boolean>(false);

  const main = useContext(MainContext);

  const [theme, settheme] = useState<any>(
    createTheme({
      breakpoints: {
        values: {
          xs: 0,
          sm: 640,
          md: 768, //900
          lg: 1280,
          xl: 1440,
        },
      },
      typography: {
        fontFamily: font400,
        H1_ExtraLight_40px: {
          fontFamily: font200,
          fontWeight: 200,
          fontSize: "40px",
          lineHeight: "60px",
        },
        H1_ExtraLight_36px: {
          fontFamily: font200,
          fontWeight: 200,
          fontSize: "36px",
          lineHeight: "54px",
        },
        H1_ExtraLight_32px: {
          fontFamily: font200,
          fontWeight: 200,
          fontSize: "32px",
          lineHeight: "48px",
        },
        H2_ExtraLight_28px: {
          fontFamily: font200,
          fontWeight: 200,
          fontSize: "28px",
          lineHeight: "42px",
        },
        H3_ExtraLight_25px: {
          fontFamily: font200,
          fontWeight: 200,
          fontSize: "25px",
          lineHeight: "37px",
        },
        H4_ExtraLight_22px: {
          fontFamily: font200,
          fontWeight: 200,
          fontSize: "22px",
          lineHeight: "33px",
        },
        H5_ExtraLight_20px: {
          fontFamily: font200,
          fontWeight: 200,
          fontSize: "20px",
          lineHeight: "30px",
        },
        H6_ExtraLight_18px: {
          fontFamily: font200,
          fontWeight: 200,
          fontSize: "18px",
          lineHeight: "27px",
        },
        Paragraph_ExtraLight_16px: {
          fontFamily: font200,
          fontWeight: 200,
          fontSize: "16px",
          lineHeight: "24px",
        },
        Paragraph_ExtraLight_14px: {
          fontFamily: font200,
          fontWeight: 200,
          fontSize: "14px",
          lineHeight: "21px",
        },
        Hint_ExtraLight_12px: {
          fontFamily: font200,
          fontWeight: 200,
          fontSize: "12px",
          lineHeight: "18px",
        },
        Hint_ExtraLight_10px: {
          fontFamily: font200,
          fontWeight: 200,
          fontSize: "10px",
          lineHeight: "15px",
        },
        H1_Light_40px: {
          fontFamily: font300,
          fontWeight: 300,
          fontSize: "40px",
          lineHeight: "60px",
        },
        H1_Light_36px: {
          fontFamily: font300,
          fontWeight: 300,
          fontSize: "36px",
          lineHeight: "54px",
        },
        H1_Light_32px: {
          fontFamily: font300,
          fontWeight: 300,
          fontSize: "32px",
          lineHeight: "48px",
        },
        H2_Light_28px: {
          fontFamily: font300,
          fontWeight: 300,
          fontSize: "28px",
          lineHeight: "42px",
        },
        H3_Light_25px: {
          fontFamily: font300,
          fontWeight: 300,
          fontSize: "25px",
          lineHeight: "37px",
        },
        H4_Light_22px: {
          fontFamily: font300,
          fontWeight: 300,
          fontSize: "22px",
          lineHeight: "33px",
        },
        H5_Light_20px: {
          fontFamily: font300,
          fontWeight: 300,
          fontSize: "20px",
          lineHeight: "30px",
        },
        H6_Light_18px: {
          fontFamily: font300,
          fontWeight: 300,
          fontSize: "18px",
          lineHeight: "27px",
        },
        Paragraph_Light_16px: {
          fontFamily: font300,
          fontWeight: 300,
          fontSize: "16px",
          lineHeight: "24px",
        },
        Paragraph_Light_14px: {
          fontFamily: font300,
          fontWeight: 300,
          fontSize: "14px",
          lineHeight: "21px",
        },
        Paragraph_Light_12px: {
          fontFamily: font300,
          fontWeight: 300,
          fontSize: "12px",
          lineHeight: "18px",
        },
        Hint_Light_12px: {
          fontFamily: font300,
          fontWeight: 300,
          fontSize: "12px",
          lineHeight: "18px",
        },
        Hint_Light_10px: {
          fontFamily: font300,
          fontWeight: 300,
          fontSize: "10px",
          lineHeight: "15px",
        },
        H1_Regular_40px: {
          fontFamily: font400,
          fontWeight: 400,
          fontSize: "40px",
          lineHeight: "60px",
        },
        H1_Regular_36px: {
          fontFamily: font400,
          fontWeight: 400,
          fontSize: "36px",
          lineHeight: "54px",
        },
        H1_Regular_32px: {
          fontFamily: font400,
          fontWeight: 400,
          fontSize: "32px",
          lineHeight: "48px",
        },
        H2_Regular_28px: {
          fontFamily: font400,
          fontWeight: 400,
          fontSize: "28px",
          lineHeight: "42px",
        },
        H3_Regular_25px: {
          fontFamily: font400,
          fontWeight: 400,
          fontSize: "25px",
          lineHeight: "37px",
        },
        H4_Regular_22px: {
          fontFamily: font400,
          fontWeight: 400,
          fontSize: "22px",
          lineHeight: "33px",
        },
        H5_Regular_20px: {
          fontFamily: font400,
          fontWeight: 400,
          fontSize: "20px",
          lineHeight: "30px",
        },
        H6_Regular_18px: {
          fontFamily: font400,
          fontWeight: 400,
          fontSize: "18px",
          lineHeight: "27px",
        },

        Paragraph_Regular_16px: {
          fontFamily: font400,
          fontWeight: 400,
          fontSize: "16px",
          lineHeight: "24px",
        },
        Paragraph_Regular_14px: {
          fontFamily: font400,
          fontWeight: 400,
          fontSize: "14px",
          lineHeight: "21px",
        },
        Hint_Regular_12px: {
          fontFamily: font400,
          fontWeight: 400,
          fontSize: "12px",
          lineHeight: "18px",
        },
        Hint_Regular_10px: {
          fontFamily: font400,
          fontWeight: 400,
          fontSize: "10px",
          lineHeight: "15px",
        },
        H1_Medium_40px: {
          fontFamily: font500,
          fontWeight: 500,
          fontSize: "40px",
          lineHeight: "60px",
        },
        H1_Medium_36px: {
          fontFamily: font500,
          fontWeight: 500,
          fontSize: "36px",
          lineHeight: "54px",
        },
        H1_Medium_32px: {
          fontFamily: font500,
          fontWeight: 500,
          fontSize: "32px",
          lineHeight: "48px",
        },
        H2_Medium_28px: {
          fontFamily: font500,
          fontWeight: 500,
          fontSize: "28px",
          lineHeight: "42px",
        },
        H3_Medium_25px: {
          fontFamily: font500,
          fontWeight: 500,
          fontSize: "25px",
          lineHeight: "37px",
        },
        H4_Medium_22px: {
          fontFamily: font500,
          fontWeight: 500,
          fontSize: "22px",
          lineHeight: "33px",
        },
        H5_Medium_20px: {
          fontFamily: font500,
          fontWeight: 500,
          fontSize: "20px",
          lineHeight: "30px",
        },
        H6_Medium_18px: {
          fontFamily: font500,
          fontWeight: 500,
          fontSize: "18px",
          lineHeight: "27px",
        },
        Paragraph_Medium_16px: {
          fontFamily: font500,
          fontWeight: 500,
          fontSize: "16px",
          lineHeight: "24px",
        },
        Paragraph_Medium_14px: {
          fontFamily: font500,
          fontWeight: 500,
          fontSize: "14px",
          lineHeight: "21px",
        },
        Hint_Medium_12px: {
          fontFamily: font500,
          fontWeight: 500,
          fontSize: "12px",
          lineHeight: "18px",
        },
        Hint_Medium_10px: {
          fontFamily: font500,
          fontWeight: 500,
          fontSize: "10px",
          lineHeight: "18px",
        },
        H1_Bold_40px: {
          fontFamily: font700,
          fontWeight: "bold",
          fontSize: "40px",
          lineHeight: "60px",
        },
        H1_Bold_36px: {
          fontFamily: font700,
          fontWeight: "bold",
          fontSize: "36px",
          lineHeight: "54px",
        },
        H1_Bold_32px: {
          fontFamily: font700,
          fontWeight: "bold",
          fontSize: "32px",
          lineHeight: "48px",
        },
        H2_Bold_28px: {
          fontFamily: font700,
          fontWeight: "bold",
          fontSize: "28px",
          lineHeight: "42px",
        },
        H3_Bold_25px: {
          fontFamily: font700,
          fontWeight: "bold",
          fontSize: "25px",
          lineHeight: "37px",
        },
        H4_Bold_22px: {
          fontFamily: font700,
          fontWeight: "bold",
          fontSize: "22px",
          lineHeight: "33px",
        },
        H5_Bold_20px: {
          fontFamily: font700,
          fontWeight: "bold",
          fontSize: "20px",
          lineHeight: "30px",
        },
        H6_Bold_18px: {
          fontFamily: font700,
          fontWeight: "bold",
          fontSize: "18px",
          lineHeight: "27px",
        },
        Paragraph_Bold_16px: {
          fontFamily: font700,
          fontWeight: "bold",
          fontSize: "16px",
          lineHeight: "24px",
        },
        Paragraph_Bold_14px: {
          fontFamily: font700,
          fontWeight: "bold",
          fontSize: "14px",
          lineHeight: "21px",
        },
        Hint_Bold_12px: {
          fontFamily: font700,
          fontWeight: "bold",
          fontSize: "12px",
          lineHeight: "18px",
        },
        H1_Black_40px: {
          fontFamily: font900,
          fontWeight: 900,
          fontSize: "40px",
          lineHeight: "60px",
        },
        H1_Black_36px: {
          fontFamily: font900,
          fontWeight: 900,
          fontSize: "36px",
          lineHeight: "54px",
        },
        H1_Black_32px: {
          fontFamily: font900,
          fontWeight: 900,
          fontSize: "32px",
          lineHeight: "48px",
        },
        H2_Black_28px: {
          fontFamily: font900,
          fontWeight: 900,
          fontSize: "28px",
          lineHeight: "42px",
        },
        H3_Black_25px: {
          fontFamily: font900,
          fontWeight: 900,
          fontSize: "25px",
          lineHeight: "37px",
        },
        H4_Black_22px: {
          fontFamily: font900,
          fontWeight: 900,
          fontSize: "22px",
          lineHeight: "33px",
        },
        H5_Black_20px: {
          fontFamily: font900,
          fontWeight: 900,
          fontSize: "20px",
          lineHeight: "30px",
        },
        H6_Black_18px: {
          fontFamily: font900,
          fontWeight: 900,
          fontSize: "18px",
          lineHeight: "27px",
        },
        Paragraph_Black_16px: {
          fontFamily: font900,
          fontWeight: 900,
          fontSize: "16px",
          lineHeight: "24px",
        },
        Paragraph_Black_14px: {
          fontFamily: font900,
          fontWeight: 900,
          fontSize: "14px",
          lineHeight: "21px",
        },
        Hint_Black_12px: {
          fontFamily: font900,
          fontWeight: 900,
          fontSize: "12px",
          lineHeight: "18px",
        },

        button: {
          textTransform: "unset",
          fontSize: "14px",
          lineHeight: "19.07px",
        },
      },
      config: {
        card: {
          boxShadow: "0px 8px 100px rgba(73, 28, 100, 0.04)",
          borderRadius: "20px",
        },
        glassmorphism: {
          backdropFilter: "blur(10px)",
        },
        flexcolumncenter: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        },
        center: {
          display: "flex",
          alignItems: "center",
        },
        jusCenter: {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        },
        spaceBetween: {
          display: "flex",
          justifyContent: "space-between",
        },

        spaceAround: {
          display: "flex",
          justifyContent: "space-around",
        },
        btn: {
          borderRadius: "24px",
          boxShadow: "unset",
          "&:hover": {
            boxShadow: "unset",
          },
          "&:disabled": {
            opacity: "0.5",
            color: "#FFFFFF",
          },
        },
        topbar: {
          height: 60,
        },
      },
    })
  );

  useMemo(() => {
    if (main.theme) {
      const newTheme =
        cookies[themeStorage] !== undefined
          ? main.theme[cookies[themeStorage]]
          : main.theme[main.theme["theme"]];

      const stateTheme =
        cookies[themeStorage] !== undefined
          ? cookies[themeStorage]
          : main.theme["theme"];
      if (newTheme) {
        settheme({
          ...theme,
          palette: ThemeConfig(newTheme)[stateTheme],
          components: ComponentsOverrides({
            ...theme,
            palette: ThemeConfig(newTheme)[stateTheme],
          }),
        });
      } else {
        settheme({
          ...theme,
          palette: defaultTheme,
          components: ComponentsOverrides({
            ...theme,
            palette: defaultTheme,
          }),
        });
      }
    }
    return () => {};
  }, [main.theme]);

  const Theme = useMemo(() => {
    if (main.theme) {
      cookies[themeStorage] === undefined &&
        setCookie(null, themeStorage, "light", {
          path: "/",
          expires: moment().add(7, "days").toDate(),
          sameSite: "strict",
        });
      return createTheme(theme);
    }
  }, [theme, main.theme]);

  useEffect(() => {
    setloading(true);
    if (main.theme && Theme) {
      setloading(false);
    }
    return () => {};
  }, [main.theme, Theme]);

  return (
    <ThemeContext.Provider value={{ settheme, loading }}>
      {/* {main.theme && Theme ? (
        <ThemeProvider theme={Theme}>{props.children}</ThemeProvider>
      ) : (
        <InitLoading loading={loading} />
      )} */}
      {main.theme && Theme ? (
        <ThemeProvider theme={Theme ? Theme : {}}>
          {props.children}
        </ThemeProvider>
      ) : (
        props.children
      )}
    </ThemeContext.Provider>
  );
};
export const ThemeContextConsumer = ThemeContext.Consumer;

export const useCustomTheme = () => {
  const context = useContext(ThemeContext);

  if (!context)
    throw new Error("useCustomTheme context must be use inside ThemeProvider");

  return context;
};
